import type { Address } from "viem"
import type { Config } from "wagmi"

import type { EmitEvent } from "@/domains/core"
import type { IEventBus } from "@/domains/events"
import type { EventEmitter } from "@/domains/library"
import type { TransactionReturnType } from "@/domains/on-chain"
import { ManualClLiquidityDecreasedEvent } from "@/domains/thick-v1/events"

import { decreaseThickNfpPosition } from "@/domains/on-chain/transactions/thick-v1/decrease-thick-nfp-position"

/**
 * Decrease-liquidity interaction for a thick-v1 position.
 *
 * The store passes `liquidityToWithdraw` and the slippage-adjusted mins it
 * computed from `getAmountsForLiquidity`; this interaction does not re-check
 * balances (the store already validates inputs against `position.liquidity`).
 *
 * Emits `ManualClLiquidityDecreasedEvent` on success so the position
 * orchestrator re-runs its GraphQL + on-chain queries.
 */
export interface DecreaseThickNfpInteractionParams {
  account: Address
  chainId: number
  config: Config
  tokenId: bigint
  poolAddress: Address
  /** Slippage-adjusted min amount0 returned by the NFPM's decreaseLiquidity. */
  amount0Min: bigint
  /** Slippage-adjusted min amount1 returned by the NFPM's decreaseLiquidity. */
  amount1Min: bigint
  /** User-selected slippage. Transaction helper re-quotes live chain output before encoding. */
  slippagePercent: number
  /** Liquidity delta to remove from the position. */
  liquidityToWithdraw: bigint
  /**
   * Optional: the remaining liquidity after withdrawal. Used only for the
   * event payload so orchestrators can short-circuit re-hydration.
   */
  liquidityRemaining?: bigint
  /** Unix-seconds deadline for the tx (inclusive). */
  deadline: number
  nfpManagerAddress: Address
  /** Human-readable label used in toast messages (e.g. `"WETH/USDC"`). */
  pairDisplayName: string
  emitter: EventEmitter<EmitEvent>
  eventBus: IEventBus
}

export const decreaseThickNfpInteraction = async ({
  account,
  chainId,
  config,
  tokenId,
  poolAddress,
  amount0Min,
  amount1Min,
  slippagePercent,
  liquidityToWithdraw,
  liquidityRemaining,
  deadline,
  nfpManagerAddress,
  pairDisplayName,
  emitter,
  eventBus,
}: DecreaseThickNfpInteractionParams): Promise<TransactionReturnType> => {
  const result = await decreaseThickNfpPosition({
    account,
    chainId,
    config,
    tokenId,
    liquidity: liquidityToWithdraw,
    amount0Min,
    amount1Min,
    slippagePercent,
    deadline,
    nfpManagerAddress,
  })

  if (result.success) {
    emitter.emit("decrease-thick-nfp", {
      title: `Decreased ${pairDisplayName} position`,
      variant: "success",
    })
    eventBus.publish(
      new ManualClLiquidityDecreasedEvent({
        poolAddress,
        tokenId,
        liquidityRemaining,
        transactionHash: "tx" in result ? result.tx : undefined,
      }),
    )
    return result
  }

  emitter.emit("decrease-thick-nfp", {
    title: "Unable to decrease position",
    description: "error" in result ? result.error : undefined,
    variant: "error",
  })

  return result
}
